/**
 * yDocToHtml — headless Y.Doc → HTML conversion.
 *
 * Converts a Y.Doc to an HTML string using TipTap's `generateHTML()`
 * with the headless extension set.
 *
 * **IMPORTANT:** The caller must ensure the Y.Doc is fully hydrated
 * (all updates applied) before calling this function. A Y.Doc returned
 * by `db.totodo.get()` or `bulkGet()` is an EMPTY shell — updates live
 * in `$totodo.doc_updates` and are only applied by `DexieYProvider.load()`.
 *
 * @see docProcessor.ts — loads via DexieYProvider before calling this
 * @see headlessExtensions.ts for the extension set
 */

import { yDocToProsemirrorJSON } from "@tiptap/y-tiptap"
import { generateHTML } from "@tiptap/core"
import type { Doc as YDoc } from "yjs"
import { headlessExtensions } from "./headlessExtensions"

/**
 * Y.Doc XmlFragment name used by TipTap's Collaboration extension.
 * Must match the `fragment` option in `Collaboration.configure()`.
 */
const XML_FRAGMENT_NAME = "default"

/**
 * Convert a Y.Doc to an HTML string using TipTap's rendering pipeline.
 *
 * @param yDoc — A hydrated Y.Doc instance (from Dexie row's `doc` property)
 * @returns HTML string, or empty string if the doc is empty/invalid
 */
export function yDocToHtml(yDoc: YDoc): string {
  try {
    const json = yDocToProsemirrorJSON(yDoc, XML_FRAGMENT_NAME)
    return generateHTML(json, headlessExtensions)
  } catch (error) {
    console.warn("[yDocToHtml] Failed to convert Y.Doc to HTML:", error)
    return ""
  }
}

/**
 * Check if an HTML string represents an empty document.
 * TipTap generates `<p></p>` for empty docs.
 */
export function isEmptyDocHtml(html: string | undefined | null): boolean {
  if (!html) return true
  const trimmed = html.trim()
  return trimmed === "" || trimmed === "<p></p>"
}
